import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import ConfigContext from '../../../contexts/ConfigContext';
import { getCityLogos } from '../../../data/cityLogos';

import styles from './LogoList.module.postcss';

// Pick the right logo slot for the props passed by the caller. Precedence
// matches the legacy if/else chain so behavior is preserved for any
// caller still passing more than one slot prop. Falls through to `common`
// when a city does not define the requested slot — this is the fix for
// the bug where Austin pages rendered Seattle's hardcoded slot arrays
// because the legacy `if (residential) { logos = RESIDENTIAL_LOGOS }`
// chain ran before the city check.
function pickSlot(slots, { residential, breweries, community, premiumPromo }) {
  if (residential && slots.residential) {
    return { logos: slots.residential, organizations: 'companies' };
  }
  if (breweries && slots.breweries) {
    return { logos: slots.breweries, organizations: 'locations' };
  }
  if (community && slots.community) {
    return { logos: slots.community, organizations: 'companies' };
  }
  if (premiumPromo && slots.premiumPromo) {
    return { logos: slots.premiumPromo, organizations: 'companies' };
  }
  return { logos: slots.common, organizations: 'companies' };
}

function LogoList({
  title,
  titleClassName,
  residential,
  breweries,
  community,
  premiumPromo,
  className,
}) {
  const { city_name } = useContext(ConfigContext);
  const slots = getCityLogos(city_name);
  const { logos, organizations } = pickSlot(slots, {
    residential,
    breweries,
    community,
    premiumPromo,
  });

  // Render the logo set twice so the marquee can translateX(-50%) and
  // appear seamless. Stable keys (passKey-alt) ensure SSR/CSR markup
  // matches — no Math.random / Date.now anywhere in this render path.
  //
  // Why NOT loading="lazy": the container uses `content-visibility:auto`
  // which already skips render work while off-screen. On top of that,
  // `loading="lazy"` combined with a 0×0 img box (which happens when an
  // SVG hasn't loaded yet inside a flex row) creates a chicken-and-egg
  // trap where the IntersectionObserver never fires. Using eager +
  // decoding="async" keeps the img visible without ever blocking paint.
  const renderPass = (passKey) => logos.map((logo) => (
    <div key={`${passKey}-${logo.alt}`} className={styles.LogoList_item}>
      <img
        src={logo.src}
        alt={logo.alt}
        decoding='async'
        width='180'
        height='100'
        style={{ objectFit: 'contain' }}
      />
    </div>
  ));

  const displayTitle = title || `Some of the ${organizations} we work with`;
  const titleClasses = `u-phosphate u-textCenter ${titleClassName || ''}`.trim();

  return (
    <div className={className}>
      {!premiumPromo && <h2 className={titleClasses}>{displayTitle}</h2>}
      <div className={styles.LogoList} aria-label='Trusted partners'>
        <div className={styles.LogoList_track}>
          {renderPass('a')}
          {renderPass('b')}
        </div>
      </div>
    </div>
  );
}

LogoList.propTypes = {
  title: PropTypes.string,
  titleClassName: PropTypes.string,
  residential: PropTypes.bool,
  breweries: PropTypes.bool,
  community: PropTypes.bool,
  premiumPromo: PropTypes.bool,
  className: PropTypes.string,
};

LogoList.defaultProps = {
  title: undefined,
  titleClassName: undefined,
  residential: false,
  breweries: false,
  community: false,
  premiumPromo: false,
  className: undefined,
};

export default LogoList;
